import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Store, Plus, Pencil, Trash2, Package, BarChart3, AlertCircle,
  Upload, X, CheckCircle2, Truck, Loader2, Image as ImageIcon, Phone, IdCard, Users,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import Navigation from '../components/Navigation';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import MyFoodDocsManager from '../components/MyFoodDocsManager';

const CATS = ['fashion', 'beauty', 'myfood', 'art', 'music', 'handcraft',
              'electronics', 'home', 'wellness', 'services', 'education',
              'kids', 'sport', 'jewelry', 'books', 'other'];

const CURRENCIES = ['eur', 'usd', 'gbp', 'chf', 'cad', 'xof', 'xaf', 'htg',
                    'mxn', 'brl', 'ngn', 'jpy', 'cny', 'inr'];

const ShopManage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [shops, setShops] = useState([]);
  const [activeShop, setActiveShop] = useState(null);
  const [tab, setTab] = useState('products'); // products | orders | claims | analytics | settings
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const isPro = (user?.account_type || '').toUpperCase() === 'PRO' || !!user?.is_admin;

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const load = async () => {
    if (!isPro) { setLoading(false); return; }
    try {
      const r = await axios.get('/api/shop/shops/mine/list');
      setShops(r.data?.shops || []);
      if (r.data?.shops?.length) setActiveShop(r.data.shops[0]);
    } finally { setLoading(false); }
  };

  if (!isPro) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
          <h2 className="text-xl font-bold mb-2">{t('eshop.pro_required', 'Compte PRO requis')}</h2>
          <p className="text-sm text-gray-400 mb-4">{t('eshop.pro_required_desc', 'Seuls les comptes PRO peuvent créer une boutique sur Destine.')}</p>
          <button onClick={() => navigate('/feed')} className="px-4 py-2 rounded-full bg-fuchsia-600">{t('common.back', 'Retour')}</button>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-950 via-black to-violet-950 text-white pb-24" data-testid="shop-manage">
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></button>
          <h1 className="text-lg font-bold flex items-center gap-2 flex-1">
            <Store className="w-5 h-5 text-fuchsia-400" /> {t('eshop.my_shops', 'Mes boutiques')}
          </h1>
          {shops.length < 10 && (
            <button onClick={() => setCreating(true)} className="px-3 py-1.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-xs font-semibold flex items-center gap-1" data-testid="create-shop-btn">
              <Plus className="w-3.5 h-3.5" /> {t('eshop.new', 'Nouvelle')}
            </button>
          )}
        </div>
        {shops.length > 1 && (
          <div className="flex overflow-x-auto gap-1.5 mt-3 scrollbar-hide" data-testid="shop-switcher">
            {shops.map(s => (
              <button key={s.id} onClick={() => setActiveShop(s)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${activeShop?.id === s.id ? 'bg-fuchsia-600' : 'bg-white/5'}`}
                data-testid={`shop-tab-${s.id}`}>
                {s.name}
              </button>
            ))}
          </div>
        )}
      </header>

      {shops.length === 0 ? (
        <div className="px-4 py-20 text-center">
          <Store className="w-12 h-12 mx-auto mb-2 text-gray-500" />
          <p className="text-sm text-gray-400 mb-4">{t('eshop.no_shop_yet', "Vous n'avez pas encore de boutique")}</p>
          <button onClick={() => setCreating(true)} className="px-4 py-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-semibold" data-testid="create-shop-empty">
            {t('eshop.create_first', 'Créer ma boutique')}
          </button>
        </div>
      ) : activeShop && (
        <>
          <nav className="flex overflow-x-auto border-b border-white/10 px-1 scrollbar-hide" data-testid="shop-manage-tabs">
            {[
              ['products', t('eshop.products', 'Produits'), Package],
              ['orders', t('eshop.orders', 'Commandes'), Truck],
              // v50.2 — "Livreurs" tab only visible for MyFood shops.
              ...(activeShop.category === 'myfood' ? [['couriers', t('eshop.couriers', 'Livreurs'), Users]] : []),
              ['claims', t('eshop.claims', 'Réclamations'), AlertCircle],
              ['analytics', t('eshop.analytics', 'Ventes'), BarChart3],
              ['settings', t('eshop.settings', 'Réglages'), Pencil],
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`shrink-0 px-3 py-3 text-xs font-semibold flex items-center gap-1.5 border-b-2 ${
                  tab === k ? 'text-fuchsia-300 border-fuchsia-500' : 'text-gray-400 border-transparent'
                }`}
                data-testid={`shop-manage-tab-${k}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </nav>
          <main className="px-4 py-4">
            {tab === 'products' && <ProductsTab shop={activeShop} />}
            {tab === 'orders' && <OrdersTab shop={activeShop} />}
            {tab === 'couriers' && activeShop.category === 'myfood' && <CouriersTab shop={activeShop} />}
            {tab === 'claims' && <ClaimsTab shop={activeShop} />}
            {tab === 'analytics' && <AnalyticsTab shop={activeShop} />}
            {tab === 'settings' && <SettingsTab shop={activeShop} onUpdated={load} onDeleted={() => { setActiveShop(null); load(); }} />}
          </main>
        </>
      )}

      {creating && <ShopFormModal onClose={() => setCreating(false)} onCreated={(s) => { setCreating(false); load(); setActiveShop(s); }} />}
      <Navigation />
    </div>
  );
};

// -------------------- Tabs --------------------

const ProductsTab = ({ shop }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);   // null | 'new' | product
  const load = async () => {
    const r = await axios.get(`/api/shop/shops/${shop.id}/products`);
    setItems(r.data?.products || []);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [shop.id]);

  const del = async (id) => {
    if (!window.confirm(t('common.confirm_delete', 'Supprimer ?'))) return;
    await axios.delete(`/api/shop/shops/${shop.id}/products/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setEditing('new')} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-semibold flex items-center justify-center gap-2" data-testid="add-product-btn">
        <Plus className="w-4 h-4" /> {t('eshop.add_product', 'Nouveau produit / service')}
      </button>
      <div className="grid grid-cols-2 gap-3">
        {items.map(p => (
          <div key={p.id} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            {p.photos?.[0]
              ? <img src={getMediaUrl(p.photos[0])} alt={p.title} className="w-full aspect-square object-cover" />
              : <div className="w-full aspect-square bg-white/5 flex items-center justify-center"><ImageIcon className="w-8 h-8 text-white/30" /></div>}
            <div className="p-2 text-xs">
              <p className="font-semibold truncate">{p.title}</p>
              <p className="text-fuchsia-300">{(p.price_cents / 100).toFixed(2)} {(p.currency || 'eur').toUpperCase()}</p>
              <div className="flex gap-1 mt-1">
                <button onClick={() => setEditing(p)} className="flex-1 py-1 bg-white/10 rounded-full text-[10px]"><Pencil className="w-3 h-3 inline" /></button>
                <button onClick={() => del(p.id)} className="flex-1 py-1 bg-red-500/20 rounded-full text-[10px] text-red-300"><Trash2 className="w-3 h-3 inline" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <ProductFormModal shop={shop} product={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const OrdersTab = ({ shop }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [tracking, setTracking] = useState({});
  const [couriers, setCouriers] = useState([]);
  const isMyFood = shop.category === 'myfood';
  useEffect(() => {
    axios.get(`/api/shop/shops/${shop.id}/orders`).then(r => setItems(r.data?.orders || []));
    if (isMyFood) {
      axios.get(`/api/shop/shops/${shop.id}/couriers`).then(r => setCouriers(r.data?.couriers || [])).catch(() => {});
    }
  }, [shop.id, isMyFood]);

  const ship = async (orderId) => {
    const num = (tracking[orderId] || '').trim();
    if (!num) return toast.error(t('eshop.tracking_required', 'N° de suivi requis'));
    await axios.post(`/api/shop/orders/${orderId}/shipped`, { tracking_number: num });
    toast.success(t('eshop.marked_shipped', 'Commande marquée comme expédiée'));
    const r = await axios.get(`/api/shop/shops/${shop.id}/orders`);
    setItems(r.data?.orders || []);
  };

  const assignCourier = async (orderId, courierId) => {
    try {
      await axios.post(`/api/shop/orders/${orderId}/assign-courier`, {
        courier_id: courierId || null,
      });
      const r = await axios.get(`/api/shop/shops/${shop.id}/orders`);
      setItems(r.data?.orders || []);
      toast.success(t('eshop.courier_assigned', 'Livreur assigné'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-8">{t('eshop.no_orders', 'Aucune commande')}</p>;

  return (
    <div className="space-y-2">
      {items.map(o => {
        const assigned = couriers.find(c => c.id === o.courier_id);
        return (
        <div key={o.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`order-${o.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{o.product_title}</p>
              <p className="text-xs text-gray-400">{o.buyer_name} · {o.buyer_email} · {o.buyer_phone}</p>
              <p className="text-xs text-gray-400 line-clamp-2">{o.buyer_address}</p>
              <p className="text-xs text-fuchsia-300 mt-1">{(o.amount_cents / 100).toFixed(2)} {o.currency?.toUpperCase()} · {o.quantity}× · net {((o.amount_cents - o.commission_cents) / 100).toFixed(2)}</p>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${o.status === 'shipped' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-yellow-500/20 text-yellow-300'}`}>{o.status}</span>
          </div>
          {o.status === 'paid' && (
            <div className="flex gap-2 mt-2">
              <input value={tracking[o.id] || ''} onChange={e => setTracking(prev => ({ ...prev, [o.id]: e.target.value }))}
                placeholder={t('eshop.tracking_number', 'N° de suivi')} className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs" data-testid={`tracking-input-${o.id}`} />
              <button onClick={() => ship(o.id)} className="px-3 py-1.5 rounded-full bg-emerald-600 text-xs font-semibold" data-testid={`ship-btn-${o.id}`}><Truck className="w-3 h-3 inline" /></button>
            </div>
          )}
          {o.tracking_number && <p className="text-[11px] text-emerald-300 mt-1"><Truck className="w-3 h-3 inline mr-1" />{o.tracking_number}</p>}

          {/* MyFood courier assignment (only for MyFood shops) */}
          {isMyFood && (
            <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
              <div className="text-[11px] text-white/60 flex items-center gap-1">
                <Users className="w-3 h-3" /> {t('eshop.orderCourier', 'Livreur')}
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={o.courier_id || ''}
                  onChange={(e) => assignCourier(o.id, e.target.value)}
                  className="flex-1 bg-white/10 border border-white/15 rounded-lg px-2 py-1.5 text-xs"
                  data-testid={`order-courier-select-${o.id}`}
                >
                  <option value="" className="bg-gray-900">{t('eshop.noCourier', '— Aucun livreur —')}</option>
                  {couriers.map(c => (
                    <option key={c.id} value={c.id} className="bg-gray-900">{c.first_name} {c.last_name}</option>
                  ))}
                </select>
                {assigned && assigned.phone && (
                  <a
                    href={`tel:${assigned.phone}`}
                    className="p-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex-shrink-0"
                    title={t('eshop.callCourier', 'Appeler le livreur')}
                    data-testid={`order-courier-call-${o.id}`}
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              {assigned && (
                <p className="text-[10px] text-emerald-300 mt-0.5">
                  ✓ {assigned.first_name} {assigned.last_name} · {assigned.phone}
                </p>
              )}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

// ============================ Couriers (MyFood) ============================
const CouriersTab = ({ shop }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { url, name }

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/shop/shops/${shop.id}/couriers`);
      setItems(r.data?.couriers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [shop.id]);

  const del = async (id) => {
    if (!window.confirm(t('eshop.confirmDeleteCourier', 'Supprimer ce livreur ?'))) return;
    try {
      await axios.delete(`/api/shop/shops/${shop.id}/couriers/${id}`);
      toast.success(t('eshop.courierDeleted', 'Livreur supprimé'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-3" data-testid="couriers-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/60">
          {t('eshop.couriersHint', "Gérez les livreurs qui travaillent pour votre restaurant MyFood.")}
        </p>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="px-3 py-1.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-xs font-semibold flex items-center gap-1"
          data-testid="courier-add-btn"
        >
          <Plus className="w-3 h-3" /> {t('eshop.addCourier', 'Ajouter un livreur')}
        </button>
      </div>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      ) : items.length === 0 ? (
        <p className="text-center text-sm text-white/40 py-8">{t('eshop.noCouriersYet', 'Aucun livreur enregistré')}</p>
      ) : (
        items.map(c => (
          <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3" data-testid={`courier-row-${c.id}`}>
            {c.id_document_url ? (
              <button
                type="button"
                onClick={() => setLightbox({ url: c.id_document_url, name: `${c.first_name} ${c.last_name}` })}
                className="w-16 h-16 rounded-xl overflow-hidden bg-black/40 flex-shrink-0 border border-white/10 hover:border-fuchsia-400"
                data-testid={`courier-doc-btn-${c.id}`}
              >
                <img src={getMediaUrl(c.id_document_url)} alt="ID" className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-black/40 flex-shrink-0 border border-white/10 flex items-center justify-center">
                <IdCard className="w-6 h-6 text-white/30" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{c.first_name} {c.last_name}</p>
              <a href={`tel:${c.phone}`} className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1 mt-0.5" data-testid={`courier-call-${c.id}`}>
                <Phone className="w-3 h-3" /> {c.phone}
              </a>
              {c.notes && <p className="text-[11px] text-white/50 mt-1 line-clamp-2">{c.notes}</p>}
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20" data-testid={`courier-edit-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => del(c.id)} className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300" data-testid={`courier-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))
      )}

      {showForm && (
        <CourierFormModal
          shop={shop}
          existing={editing}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await load(); }}
        />
      )}

      {/* ID document lightbox — tap anywhere to close */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex flex-col items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="courier-doc-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20"
            data-testid="courier-doc-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={getMediaUrl(lightbox.url)}
            alt="ID"
            className="max-w-full max-h-[80vh] rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="mt-3 text-sm text-white/80">{lightbox.name}</p>
        </div>
      )}
    </div>
  );
};

const CourierFormModal = ({ shop, existing, onClose, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    first_name: existing?.first_name || '',
    last_name: existing?.last_name || '',
    phone: existing?.phone || '',
    id_document_url: existing?.id_document_url || '',
    notes: existing?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`/api/shop/shops/${shop.id}/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, id_document_url: r.data?.url || '' }));
      toast.success(t('eshop.docUploaded', 'Document ajouté'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      return toast.error(t('eshop.courierMissing', 'Nom, prénom et téléphone requis'));
    }
    setSaving(true);
    try {
      if (existing) {
        await axios.put(`/api/shop/shops/${shop.id}/couriers/${existing.id}`, form);
      } else {
        await axios.post(`/api/shop/shops/${shop.id}/couriers`, form);
      }
      toast.success(existing ? t('eshop.courierUpdated', 'Livreur mis à jour') : t('eshop.courierCreated', 'Livreur ajouté'));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl border border-white/15 w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="courier-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">
            {existing ? t('eshop.editCourier', 'Modifier le livreur') : t('eshop.addCourier', 'Ajouter un livreur')}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={form.first_name}
            onChange={e => setForm({ ...form, first_name: e.target.value })}
            placeholder={t('eshop.firstName', 'Prénom')}
            className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm"
            data-testid="courier-form-firstname"
          />
          <input
            value={form.last_name}
            onChange={e => setForm({ ...form, last_name: e.target.value })}
            placeholder={t('eshop.lastName', 'Nom')}
            className="bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm"
            data-testid="courier-form-lastname"
          />
        </div>
        <input
          type="tel"
          value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          placeholder={t('eshop.phone', 'Téléphone')}
          className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm"
          data-testid="courier-form-phone"
        />
        <textarea
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder={t('eshop.courierNotes', 'Notes (véhicule, disponibilité…)')}
          rows={2}
          className="w-full bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm resize-none"
          data-testid="courier-form-notes"
        />
        <div>
          <label className="block text-xs text-white/60 mb-1">{t('eshop.idDocument', "Pièce d'identité (photo)")}</label>
          {form.id_document_url ? (
            <div className="relative">
              <img src={getMediaUrl(form.id_document_url)} alt="ID" className="w-full max-h-40 object-cover rounded-xl" />
              <button type="button" onClick={() => setForm(f => ({ ...f, id_document_url: '' }))} className="absolute top-2 right-2 p-1 bg-black/70 rounded-full">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-white/20 rounded-xl text-sm text-white/60 cursor-pointer hover:border-fuchsia-400 hover:text-fuchsia-300">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? t('common.uploading', 'Upload…') : t('eshop.pickDoc', 'Choisir une image')}
              <input type="file" accept="image/*" className="hidden" onChange={e => uploadDoc(e.target.files?.[0])} data-testid="courier-form-doc-input" />
            </label>
          )}
        </div>
        <button
          onClick={submit}
          disabled={saving || uploading}
          className="w-full py-2.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-sm font-semibold flex items-center justify-center gap-2"
          data-testid="courier-form-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {saving ? t('common.saving', 'Enregistrement…') : t('common.save', 'Enregistrer')}
        </button>
      </div>
    </div>
  );
};

const ClaimsTab = ({ shop }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [responding, setResponding] = useState(null);
  const [response, setResponse] = useState('');
  const load = async () => {
    const r = await axios.get(`/api/shop/shops/${shop.id}/claims`);
    setItems(r.data?.claims || []);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [shop.id]);
  const respond = async (claimId) => {
    await axios.post(`/api/shop/claims/${claimId}/respond`, { seller_response: response, status: 'resolved' });
    setResponding(null); setResponse(''); load();
    toast.success(t('common.saved', 'Enregistré'));
  };
  if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-8">{t('eshop.no_claims', 'Aucune réclamation')}</p>;
  return (
    <div className="space-y-2">
      {items.map(c => (
        <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10">
          <p className="font-semibold text-sm">{c.reason}</p>
          <p className="text-xs text-gray-300 mt-1">{c.message}</p>
          <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] ${c.status === 'open' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{c.status}</span>
          {c.seller_response && <p className="text-xs text-emerald-300 mt-2 italic">{t('eshop.your_response', 'Votre réponse')} : {c.seller_response}</p>}
          {c.status === 'open' && responding !== c.id && (
            <button onClick={() => setResponding(c.id)} className="mt-2 px-3 py-1 rounded-full bg-fuchsia-600 text-xs">{t('eshop.respond', 'Répondre')}</button>
          )}
          {responding === c.id && (
            <div className="mt-2 space-y-2">
              <textarea value={response} onChange={e => setResponse(e.target.value)} rows={2} placeholder={t('eshop.your_response_placeholder', 'Votre réponse…')} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm" />
              <button onClick={() => respond(c.id)} className="w-full py-1.5 rounded-full bg-fuchsia-600 text-xs font-semibold">{t('common.send', 'Envoyer')}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AnalyticsTab = ({ shop }) => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`/api/shop/shops/${shop.id}/analytics`).then(r => setStats(r.data));
  }, [shop.id]);
  if (!stats) return <p className="text-center py-8 text-gray-400">{t('common.loading', 'Chargement…')}</p>;
  const cur = (shop.currency || 'eur').toUpperCase();
  const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur });
  const card = (label, cents) => (
    <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="text-lg font-bold text-fuchsia-300">{fmt.format((cents || 0) / 100)}</p>
    </div>
  );
  return (
    <div className="space-y-3">
      {/* Top accounting summary — gross / commission / net */}
      <div className="grid grid-cols-3 gap-2" data-testid="shop-analytics-breakdown">
        <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
          <p className="text-[10px] text-gray-400">{t('eshop.gross', 'Brut')}</p>
          <p className="text-base font-bold text-white">{fmt.format((stats.total_gross_cents || 0) / 100)}</p>
        </div>
        <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-[10px] text-amber-200/80">{t('eshop.commission', 'Commission')} ({stats.commission_percent || 5}%)</p>
          <p className="text-base font-bold text-amber-200">−{fmt.format((stats.total_commission_cents || 0) / 100)}</p>
        </div>
        <div className="p-3 rounded-2xl bg-emerald-500/15 border border-emerald-500/40">
          <p className="text-[10px] text-emerald-200/80">{t('eshop.net_to_receive', 'Net à recevoir')}</p>
          <p className="text-base font-bold text-emerald-200" data-testid="shop-analytics-net">{fmt.format((stats.total_net_cents || 0) / 100)}</p>
        </div>
      </div>
      {/* Per-window net cards */}
      <div className="grid grid-cols-2 gap-3">
        {card(t('eshop.today', "Aujourd'hui"), stats.today_cents)}
        {card(t('eshop.this_week', 'Cette semaine'), stats.week_cents)}
        {card(t('eshop.this_month', 'Ce mois'), stats.month_cents)}
        {card(t('eshop.this_year', 'Cette année'), stats.year_cents)}
        <div className="col-span-2 p-3 rounded-2xl bg-fuchsia-500/15 border border-fuchsia-500/40">
          <p className="text-xs text-gray-300">{t('eshop.total_net', 'Total net')} ({stats.sales_count} {t('eshop.sales', 'ventes')})</p>
          <p className="text-2xl font-bold text-white">{fmt.format((stats.total_net_cents || 0) / 100)}</p>
          <p className="text-[10px] text-gray-400 mt-1">{t('eshop.after_commission', 'Après commission Destine 5%')}</p>
        </div>
      </div>
    </div>
  );
};

const SettingsTab = ({ shop, onUpdated, onDeleted }) => {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const del = async () => {
    // Two-step UX: tell the PRO this is a deletion REQUEST sent to admins
    // (the shop remains live on the public app until an admin validates).
    const confirmMsg = t(
      'eshop.confirm_request_delete_shop',
      "Demander la suppression de votre boutique ?\n\nElle disparaîtra de votre Destine Ads mais restera visible dans l'app jusqu'à validation par un administrateur (notifié immédiatement)."
    );
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await axios.delete(`/api/shop/shops/${shop.id}`);
      const msg = r?.data?.message || t(
        'eshop.shop_deletion_requested',
        "Demande envoyée — les administrateurs vont la traiter."
      );
      toast.success(msg);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  return (
    <div className="space-y-3">
      <div className="p-3 rounded-2xl bg-white/5 border border-white/10 space-y-1 text-sm">
        <p><span className="text-gray-400">{t('eshop.name', 'Nom')} :</span> <span className="font-semibold">{shop.name}</span></p>
        <p><span className="text-gray-400">{t('eshop.category', 'Catégorie')} :</span> {t(`eshop.cat_${shop.category}`) || shop.category}</p>
        <p><span className="text-gray-400">{t('eshop.country', 'Pays')} :</span> {shop.country}</p>
        <p><span className="text-gray-400">SIRET :</span> {shop.siret}</p>
        <p><span className="text-gray-400">{t('eshop.currency', 'Devise')} :</span> {(shop.currency || 'eur').toUpperCase()}</p>
        <p><span className="text-gray-400">IBAN :</span> <span className="font-mono text-[11px]">{shop.iban || '—'}</span></p>
        <p><span className="text-gray-400">BIC :</span> <span className="font-mono text-[11px]">{shop.bic || '—'}</span></p>
      </div>
      <button onClick={() => setEditing(true)} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-semibold flex items-center justify-center gap-2"><Pencil className="w-4 h-4" /> {t('eshop.edit_shop', 'Modifier la boutique')}</button>

      {/* v49.4 — MyFood restaurants: manage official documents (permit, hygiene…) */}
      <MyFoodDocsManager shop={shop} onChange={onUpdated} />

      <button onClick={del} className="w-full py-2.5 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30 font-semibold flex items-center justify-center gap-2"><Trash2 className="w-4 h-4" /> {t('eshop.request_shop_deletion', 'Demander la suppression')}</button>
      {editing && <ShopFormModal shop={shop} onClose={() => setEditing(false)} onCreated={() => { setEditing(false); onUpdated?.(); }} />}
    </div>
  );
};

// -------------------- Form modals --------------------

const ShopFormModal = ({ shop, onClose, onCreated }) => {
  const { t } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const isEdit = !!shop;
  const [form, setForm] = useState({
    name: shop?.name || '', category: shop?.category || 'fashion', country: shop?.country || 'FR',
    currency: shop?.currency || 'eur', description: shop?.description || '',
    cover_url: shop?.cover_url || '', website_url: shop?.website_url || '',
    phone: shop?.phone || '', contact_email: shop?.contact_email || '',
    address: shop?.address || '', lat: shop?.lat || '', lng: shop?.lng || '',
    siret: shop?.siret || '', company_created_at: shop?.company_created_at || '',
    shipping_terms: shop?.shipping_terms || '',
    shipping_fee_euros: shop?.shipping_fee_cents != null ? (Number(shop.shipping_fee_cents) / 100).toString() : '0',
    iban: shop?.iban || '', bic: shop?.bic || '',
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post('/api/shop/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (url) set('cover_url', url);
      else throw new Error('No URL');
    } catch (e) { toast.error(e?.response?.data?.detail || (t('common.upload_failed', 'Upload échoué'))); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name || !form.siret) return toast.error(t('eshop.fill_required', 'Champs obligatoires manquants'));
    setBusy(true);
    try {
      const shippingEuros = parseFloat(String(form.shipping_fee_euros || '0').replace(',', '.')) || 0;
      const payload = {
        name: form.name, category: form.category, country: form.country,
        currency: form.currency, description: form.description,
        cover_url: form.cover_url, website_url: form.website_url,
        phone: form.phone, contact_email: form.contact_email,
        address: form.address,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        siret: form.siret, company_created_at: form.company_created_at,
        shipping_terms: form.shipping_terms,
        shipping_fee_cents: Math.round(shippingEuros * 100),
        iban: form.iban, bic: form.bic,
      };
      if (isEdit) {
        await axios.patch(`/api/shop/shops/${shop.id}`, payload);
        toast.success(t('common.saved', 'Enregistré'));
      } else {
        const r = await axios.post('/api/shop/shops', payload);
        toast.success(t('eshop.shop_created', 'Boutique créée'));
        onCreated?.(r.data);
        return;
      }
      onCreated?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur';
      if (e?.response?.status === 402) {
        toast.error(msg);
        if (window.confirm((t('eshop.pay_extra_now') || `Payer 500 € maintenant pour cette boutique supplémentaire ?`))) {
          const r = await axios.post('/api/shop/extra/checkout');
          if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
        }
      } else toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose} data-testid="shop-form-modal"
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}>
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '90vh' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="font-semibold">{isEdit ? (t('eshop.edit_shop', 'Modifier la boutique')) : (t('eshop.create_shop', 'Créer une boutique'))}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Cover */}
          <label className="block rounded-2xl border-2 border-dashed border-white/20 hover:border-fuchsia-500/40 overflow-hidden cursor-pointer">
            {form.cover_url ? <img src={getMediaUrl(form.cover_url)} alt="" className="w-full h-32 object-cover" /> : (
              <div className="h-32 flex flex-col items-center justify-center text-gray-400">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-1" /><span className="text-xs">{t('eshop.upload_cover', 'Photo de couverture')}</span></>}
              </div>
            )}
            <input type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="shop-form-cover" />
          </label>
          <p className="text-[11px] text-gray-400 -mt-1" data-testid="shop-cover-hint">
            💡 {t('eshop.cover_dim_hint', 'Format recommandé : 1600 × 600 px (ratio paysage 8:3), JPG ou PNG, < 3 Mo. Évite les éléments importants dans les coins (recadrage mobile).')}
          </p>
          <Field2 label={t('eshop.name', 'Nom de la boutique *')} value={form.name} onChange={v => set('name', v)} testid="shop-form-name" />
          <div className="grid grid-cols-2 gap-2">
            <Select2 label={t('eshop.category', 'Catégorie')} value={form.category} onChange={v => set('category', v)} options={CATS.map(c => [c, t(`eshop.cat_${c}`) || c])} testid="shop-form-cat" />
            <Field2 label={t('eshop.country', 'Pays (ISO)')} value={form.country} onChange={v => set('country', v.toUpperCase().slice(0, 2))} testid="shop-form-country" />
          </div>
          <Select2 label={t('eshop.currency', 'Devise')} value={form.currency} onChange={v => set('currency', v)} options={CURRENCIES.map(c => [c, c.toUpperCase()])} testid="shop-form-currency" />
          <Field2 label={t('eshop.description', 'Description de l\'activité')} value={form.description} onChange={v => set('description', v)} textarea testid="shop-form-desc" />
          <Field2 label="SIRET *" value={form.siret} onChange={v => set('siret', v)} testid="shop-form-siret" />
          <Field2 label={t('eshop.company_date', "Date de création de l'entreprise")} value={form.company_created_at} onChange={v => set('company_created_at', v)} type="date" testid="shop-form-cdate" />
          <Field2 label={t('eshop.address_full', 'Adresse postale')} value={form.address} onChange={v => set('address', v)} textarea testid="shop-form-address" />
          <div className="grid grid-cols-2 gap-2">
            <Field2 label="Latitude" value={form.lat} onChange={v => set('lat', v)} type="number" testid="shop-form-lat" />
            <Field2 label="Longitude" value={form.lng} onChange={v => set('lng', v)} type="number" testid="shop-form-lng" />
          </div>
          <Field2 label={t('eshop.phone', 'Téléphone')} value={form.phone} onChange={v => set('phone', v)} testid="shop-form-phone" />
          <Field2 label="Email" value={form.contact_email} onChange={v => set('contact_email', v)} type="email" testid="shop-form-email" />
          <Field2 label={t('eshop.website', 'Site web (facultatif)')} value={form.website_url} onChange={v => set('website_url', v)} testid="shop-form-web" />
          <div className="grid grid-cols-2 gap-2">
            <Field2 label={`${t('eshop.shipping_fee', 'Frais de livraison')} (${(form.currency || 'eur').toUpperCase()})`} value={form.shipping_fee_euros} onChange={v => set('shipping_fee_euros', v)} type="number" inputMode="decimal" step="0.01" testid="shop-form-shippingfee" />
            <Field2 label={t('eshop.shipping_terms_short', 'Modalités')} value={form.shipping_terms} onChange={v => set('shipping_terms', v)} testid="shop-form-shippingterms" />
          </div>
          <p className="text-[11px] text-gray-400 text-center px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            💡 {t('eshop.payout_in_ads', 'Vos coordonnées bancaires (IBAN, BIC, raison sociale) se renseignent dans Destine Ads.')}
          </p>
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="shop-form-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {isEdit ? (t('common.save', 'Enregistrer')) : (t('eshop.create_shop', 'Créer la boutique'))}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductFormModal = ({ shop, product, onClose, onSaved }) => {
  const { t } = useLanguage();
  const kbHeight = useKeyboardHeight();
  const isEdit = !!product;
  const [form, setForm] = useState({
    kind: product?.kind || 'product',
    title: product?.title || '', description: product?.description || '',
    price_euros: product?.price_cents != null ? (Number(product.price_cents) / 100).toString() : '',
    photos: product?.photos || [],
    video_url: product?.video_url || '',
    // 'available' (stock=null, unlimited) or 'unavailable' (stock=0)
    availability: product?.stock === 0 ? 'unavailable' : 'available',
    // Auto-validation delay for services (in days). Default 7.
    delivery_days: product?.delivery_days != null ? String(product.delivery_days) : '7',
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post('/api/shop/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (url) set('photos', [...form.photos, url]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload échoué'); }
    finally { setUploading(false); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    // Quick duration check (60s max)
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      await new Promise((resolve, reject) => {
        v.preload = 'metadata';
        v.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          if (v.duration > 65) reject(new Error('Vidéo trop longue (60 s max)'));
          else resolve();
        };
        v.onerror = () => { URL.revokeObjectURL(url); resolve(); };
        v.src = url;
      });
    } catch (err) { toast.error(err.message || 'Vidéo invalide'); return; }
    const fd = new FormData(); fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post('/api/shop/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data?.url;
      if (url) set('video_url', url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload vidéo échoué'); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    const priceEuros = parseFloat(String(form.price_euros).replace(',', '.'));
    if (!form.title || form.title.trim().length < 2)
      return toast.error(t('eshop.title_required', 'Le titre du produit/service est obligatoire'));
    if (!priceEuros || isNaN(priceEuros) || priceEuros <= 0)
      return toast.error(t('eshop.price_required', 'Le prix est obligatoire (en euros, ex: 300)'));
    // Validate delivery_days for services (1..60).
    let deliveryDays = null;
    if (form.kind === 'service') {
      const d = parseInt(String(form.delivery_days || '').trim(), 10);
      if (!d || isNaN(d) || d < 1 || d > 60)
        return toast.error(t('eshop.delivery_days_invalid', 'Délai de livraison invalide (1 à 60 jours)'));
      deliveryDays = d;
    }
    // Availability: "available" => stock = null (unlimited), "unavailable" => stock = 0
    const stockValue = form.availability === 'unavailable' ? 0 : null;
    setBusy(true);
    try {
      const priceCents = Math.round(priceEuros * 100);
      const body = {
        kind: form.kind,
        title: form.title.trim(),
        description: (form.description || '').trim(),
        photos: form.photos || [],
        video_url: form.video_url || null,
        price_cents: priceCents,
        stock: stockValue,
        delivery_days: deliveryDays,
      };
      if (isEdit) await axios.patch(`/api/shop/shops/${shop.id}/products/${product.id}`, body);
      else await axios.post(`/api/shop/shops/${shop.id}/products`, body);
      toast.success(t('common.saved', 'Enregistré'));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-6 overflow-y-auto"
      onClick={onClose}
      style={{ paddingBottom: kbHeight > 0 ? kbHeight + 16 : undefined }}>
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: kbHeight > 0 ? `calc(100vh - ${kbHeight + 32}px)` : '90vh' }}
        data-testid="product-form-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="font-semibold">{isEdit ? (t('eshop.edit_product', 'Modifier')) : (t('eshop.add_product', 'Nouveau produit'))}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <Select2 label={t('eshop.kind', 'Type')} value={form.kind} onChange={v => set('kind', v)}
            options={[['product', t('eshop.kind_product', 'Produit')], ['service', t('eshop.kind_service', 'Service')]]} testid="product-form-kind" />
          <Field2 label={t('eshop.product_title_label', 'Titre du produit / service *')} value={form.title} onChange={v => set('title', v)} testid="product-form-title" />
          <Field2 label={t('eshop.description', 'Description')} value={form.description} onChange={v => set('description', v)} textarea testid="product-form-desc" />
          <Field2 label={`${t('eshop.product_price_label', 'Prix')} (${(shop.currency || 'eur').toUpperCase()}) *`} value={form.price_euros} onChange={v => set('price_euros', v)} type="number" inputMode="decimal" step="0.01" testid="product-form-price" />
          {form.kind === 'service' && (
            <div data-testid="product-form-delivery-days-wrapper">
              <Field2
                label={t('eshop.delivery_days_label', 'Délai de réalisation (en jours) *')}
                value={form.delivery_days}
                onChange={v => set('delivery_days', v)}
                type="number"
                inputMode="numeric"
                step="1"
                testid="product-form-delivery-days"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                {t('eshop.delivery_days_hint', "Au-delà de ce délai, la commande est automatiquement validée si l'acheteur n'a pas confirmé.")}
              </p>
            </div>
          )}
          {/* Photos grid */}
          <div>
            <p className="text-xs text-gray-400 mb-1">{t('eshop.photos', 'Photos')}</p>
            <div className="grid grid-cols-3 gap-2">
              {form.photos.map((p, i) => (
                <div key={i} className="relative aspect-square">
                  <img src={getMediaUrl(p)} alt="" className="w-full h-full object-cover rounded-xl" />
                  <button onClick={() => set('photos', form.photos.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 rounded-full"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {form.photos.length < 6 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-fuchsia-500/40">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5 text-gray-400" />}
                  <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
                </label>
              )}
            </div>
          </div>
          {/* Video upload */}
          <div>
            <p className="text-xs text-gray-400 mb-1">{t('eshop.video_60s', 'Vidéo (max 60 s, optionnelle)')}</p>
            {form.video_url ? (
              <div className="relative">
                <video src={getMediaUrl(form.video_url)} controls className="w-full rounded-xl" />
                <button onClick={() => set('video_url', '')} className="absolute -top-1.5 -right-1.5 p-1 bg-red-500 rounded-full" data-testid="remove-product-video"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <label className="block aspect-video rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-fuchsia-500/40">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-xs text-gray-400 flex items-center gap-1"><Upload className="w-4 h-4" /> Importer</span>}
                <input type="file" accept="video/*" onChange={uploadVideo} className="hidden" data-testid="upload-product-video" />
              </label>
            )}
          </div>
          {/* Availability toggle (replaces the old free-text stock field).
              `available` => unlimited, `unavailable` => sold-out / on hold. */}
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              {t('eshop.availability_label', 'Disponibilité')} *
            </label>
            <select
              value={form.availability}
              onChange={e => set('availability', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500"
              data-testid="product-form-availability"
            >
              <option value="available">{t('eshop.available', 'Disponible')}</option>
              <option value="unavailable">{t('eshop.unavailable', 'Indisponible')}</option>
            </select>
          </div>
          <button onClick={submit} disabled={busy} className="w-full py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 font-semibold" data-testid="product-form-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : (t('common.save', 'Enregistrer'))}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field2 = ({ label, value, onChange, type = 'text', textarea, testid, inputMode, step }) => (
  <div>
    <label className="text-xs text-gray-400 block mb-1">{label}</label>
    {textarea
      ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={2} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500" data-testid={testid} />
      : <input value={value} onChange={e => onChange(e.target.value)} type={type} inputMode={inputMode} step={step} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500" data-testid={testid} />
    }
  </div>
);

const Select2 = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs text-gray-400 block mb-1">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full bg-gray-900 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500" data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export default ShopManage;
